import * as React from 'react';
import {
  BasePortletStore,
  createPortletStore,
  OptimizedMaskable,
  PoeStateChangedCallback
} from 'ehs-react';
import { at } from './generated/Beans';
import { UserMenuStoreApi } from './stores/menuUser.store';
import { MenuUserList } from './components/MenuUserList';
import {
  cleanupNavigationBar,
  doHelpDocumentInitialization,
  scheduleRefreshAssertionCall
} from './utils';
import { initializeImprintLink } from './imprint';
import PortletConfiguration = at.sense.hooks.login.config.FrontendConfigurationBean;

type PortletStore = BasePortletStore<PortletConfiguration> & {
  /**
   * If portlet is ready.
   */
  ready: boolean;
};

/**
 * The Portlet store.
 *
 * It contains the portlet url, frontend configuration, orchestratable instance to send workflow events, and other state needed by everything, such as maybe a portlet-wide loadmask state.
 * This is automatically filled by the POE.
 *
 * usePortletStore is a hook for use inside react components, which will re-render whenever the state they request changes.
 * PortletStoreApi is for use in callbacks and async functions, not in the view.
 */
export const [
  usePortletStore,
  PortletStoreApi
] = createPortletStore<PortletStore>(() => ({
  ready: true
}));

/**
 * The main App component.
 *
 * It should render the MainView, possibly a portlet-wide loadmask and any global buttons.
 */
export function App(): JSX.Element | null {
  return (
    <OptimizedMaskable
      useStore={usePortletStore}
      selector={(state: PortletStore): boolean => !state.ready}
      className="login-hook-root-maskable"
    >
      <MenuUserList />
    </OptimizedMaskable>
  );
}

/**
 * Will be called upon state change of workflow.
 * @param event the event name
 */
export const poeStateChanged: PoeStateChangedCallback = async event => {
  if ('init' === event) {
    if (PortletStoreApi.getState().additionalParams?.signedIn) {
      UserMenuStoreApi.getState().load().then(cleanupNavigationBar);
      let { ssoRefreshTime } = PortletStoreApi.getState().configuration;

      // setup help icon:
      void doHelpDocumentInitialization();

      // delay refresh token:
      ssoRefreshTime = ssoRefreshTime ? new Date(ssoRefreshTime) : null;
      if (ssoRefreshTime) {
        console.info(
          'Got valid SAML SSO assertion refresh time',
          ssoRefreshTime
        );
        scheduleRefreshAssertionCall(ssoRefreshTime);
      } else {
        console.info(
          'No SAML SSO assertion refresh necessary for current user'
        );
      }

      window.addEventListener('keyup', function (event) {
        if (event.key === 'Escape') {
          document
            .querySelector<HTMLElement>(
              '.mobile-menu.visible .overlay, #more_actions.selected'
            )
            ?.click();
        }
      });
    }
    // do imprint handling anyway:
    initializeImprintLink();
  }
};
